import { Input, Select, Typography } from '@tractian/ui'
import { useTranslation } from 'react-i18next'

import type { MatrixUrlFailureReason } from '@/app/matrix'
import {
  CUSTOM_PRESET_KEY,
  MATRIX_PRESETS,
  MATRIX_SERVICES
} from '@/features/matrix/domain/matrix-presets'
import type { MatrixFormState } from '@/features/matrix/hooks/use-matrix-form'

// Failure reasons render through the i18n namespace like every other string —
// never the raw code.
const ERROR_KEYS: Record<MatrixUrlFailureReason, string> = {
  'invalid-url': 'errors.invalidUrl',
  'not-allowed': 'errors.urlNotAllowed'
}

export type MatrixFormProps = {
  /** Edit state, owned by useMatrixForm so the actions can live outside the form. */
  state: MatrixFormState
}

/**
 * Pure form for the matrix override: one row per backend service (Select of
 * presets + a Custom free-text URL with inline allowlist validation). Owns no
 * state and no actions — the host renders those, validating through
 * `state.submit` so a rejected URL never reaches storage.
 */
export function MatrixForm({ state }: MatrixFormProps) {
  const { t } = useTranslation('matrix')

  return (
    <div className="flex flex-col gap-4">
      {MATRIX_SERVICES.map(service => {
        const selection = state.selections[service]
        const error = state.errors[service]
        const options = [
          ...MATRIX_PRESETS[service].map(preset => ({
            value: preset.key,
            label: `${t(`presets.${preset.key}`)} — ${preset.url}`
          })),
          { value: CUSTOM_PRESET_KEY, label: t('presets.custom') }
        ]
        return (
          <div
            key={service}
            className="flex flex-col gap-2"
            data-testid={`matrix-service-${service}`}
          >
            <Typography.Text size="medium" color="title">
              {t(`services.${service}`)}
            </Typography.Text>
            <Select
              options={options}
              value={selection.presetKey}
              onValueChange={next => {
                if (Array.isArray(next) || next == null) return
                state.setPreset(service, next)
              }}
              data-testid={`matrix-preset-${service}`}
            >
              <Select.Trigger>
                <Select.Value />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content>
                  <Select.Options />
                  <Select.Empty />
                </Select.Content>
              </Select.Portal>
            </Select>
            {selection.presetKey === CUSTOM_PRESET_KEY && (
              <div className="flex flex-col gap-1">
                <Input
                  value={selection.customUrl}
                  onValueChange={next => state.setCustomUrl(service, next)}
                  error={Boolean(error)}
                  data-testid={`matrix-custom-url-${service}`}
                >
                  <Input.Field>
                    <Input.Input placeholder={t('customUrl.placeholder')} />
                  </Input.Field>
                </Input>
                {error ? (
                  <Typography.Text size="small" className="text-red-500">
                    {t(ERROR_KEYS[error])}
                  </Typography.Text>
                ) : null}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
